import ls from 'localstorage-slim';
import type { LeaderboardEntry, PlatformLeaderboards } from '@hexa/app/platform/Platform';
import RandomDataGenerator from '@hexa/libs/RandomDataGenerator';
import fakeUsers from '@hexa/app/leaderboard/fakeUsers.json';

export class FakeLeaderboards implements PlatformLeaderboards {
  private static readonly lsKey = `hexa-dev-leaderboard`;

  private readonly rnd = new RandomDataGenerator([Math.random()]);

  public static debugClearLeaderboards(): void {
    ls.remove(this.lsKey);
  }

  private getLeaderboard(leaderboardId: string): LeaderboardEntry[] {
    const data: Record<string, LeaderboardEntry[]> | null = ls.get(FakeLeaderboards.lsKey);

    if (data?.[leaderboardId]) {
      return data[leaderboardId];
    }

    return this.generateLeaderboard(leaderboardId);
  }

  private saveLeaderboard(leaderboardId: string, leaderboard: LeaderboardEntry[]): void {
    const data: Record<string, LeaderboardEntry[]> = ls.get(FakeLeaderboards.lsKey) ?? {};
    data[leaderboardId] = leaderboard;
    ls.set(FakeLeaderboards.lsKey, data);
  }

  private generateLeaderboard(leaderboardId: string): LeaderboardEntry[] {
    const entriesCount = this.rnd.integerInRange(0, 7);

    const leaderboard = this.rnd
      .shuffle((fakeUsers as { name: string; avatarUrl: string }[]).slice())
      .slice(0, entriesCount)
      .map((fakeUser) => ({
        player: {
          name: fakeUser.name,
          avatarUrl: fakeUser.avatarUrl,
          currentUser: false,
          id: 'FAKE_0',
        },
        rank: 0,
        score: this.rnd.integerInRange(1, 300),
      }));

    leaderboard.sort((a, b) => b.score - a.score);
    leaderboard.forEach((entry, index) => {
      entry.rank = index + 1;
    });

    this.saveLeaderboard(leaderboardId, leaderboard);

    return leaderboard;
  }

  public async getPlayerEntry(leaderboardId: string): Promise<LeaderboardEntry | null> {
    return this.getLeaderboard(leaderboardId).find((entry) => entry.player.currentUser) ?? null;
  }

  public async getFriendsEntries(leaderboardId: string): Promise<LeaderboardEntry[]> {
    return this.getLeaderboard(leaderboardId);
  }

  public async getEntries(leaderboardId: string, count = 10, offset = 0): Promise<LeaderboardEntry[] | null> {
    return this.getLeaderboard(leaderboardId).slice(offset, offset + count);
  }

  public async setScore(leaderboardId: string, score: number): Promise<boolean> {
    const leaderboard = this.getLeaderboard(leaderboardId);

    const existingEntry = leaderboard.find((entry) => entry.player.currentUser);

    if (existingEntry) {
      existingEntry.score = score;
    } else {
      leaderboard.push({
        player: { name: 'You', currentUser: true, avatarUrl: null, id: '' },
        score,
        rank: 0,
      });
    }

    leaderboard.sort((entryA, entryB) => entryB.score - entryA.score);
    leaderboard.forEach((entry, index) => {
      entry.rank = index + 1;
    });

    this.saveLeaderboard(leaderboardId, leaderboard);

    return true;
  }
}
